(() => {
  const ROOT_SELECTOR = '[data-contact-inquiry-form]';
  const MAX_FILES = 4;
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const MAX_TOTAL_FILE_SIZE = 15 * 1024 * 1024;
  const SEARCH_TIMEOUT = 6000;
  const SUBMIT_MODAL_MIN_VISIBLE = 700;

  async function fetchWithTimeout(url, options = {}, timeout = SEARCH_TIMEOUT) {
    const controller = new AbortController();
    const parentSignal = options.signal;
    let timedOut = false;
    const forwardAbort = () => controller.abort();

    if (parentSignal) {
      if (parentSignal.aborted) controller.abort();
      else parentSignal.addEventListener('abort', forwardAbort, { once: true });
    }

    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeout);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (timedOut) throw new Error('Search request timed out');
      throw error;
    } finally {
      window.clearTimeout(timer);
      if (parentSignal) parentSignal.removeEventListener('abort', forwardAbort);
    }
  }

  function initializeContactForm(root) {
    if (!root || root.dataset.contactInquiryInitialized === 'true') return;
    root.dataset.contactInquiryInitialized = 'true';

    const form = root.querySelector('form');
    if (!form) return;
    form.noValidate = true;

    const panels = Array.from(root.querySelectorAll('[data-step-panel]'));
    const indicators = Array.from(root.querySelectorAll('[data-step-indicator]'));
    const confirmation = root.querySelector('[data-confirmation]');
    const submitStatus = root.querySelector('[data-submit-status]');
    const submitButton = root.querySelector('[data-submit-button]');
    const submitLabel = root.querySelector('[data-submit-label]');
    const confirmationAcknowledgement = root.querySelector('[data-confirmation-acknowledgement]');
    const submitModal = root.querySelector('[data-submit-modal]');
    const submitModalDialog = root.querySelector('[data-submit-modal-dialog]');
    const submitModalSending = root.querySelector('[data-submit-modal-sending]');
    const submitModalSuccess = root.querySelector('[data-submit-modal-success]');
    const submitReceipt = root.querySelector('[data-submit-receipt]');
    const submitAgainButton = root.querySelector('[data-submit-again]');
    const endpoint = root.dataset.submissionEndpoint || '';
    let currentStep = Number(root.dataset.initialStep || 0);
    let confirmationUrls = [];
    let isSubmitting = false;
    let submitModalOpenedAt = 0;
    let submitModalCloseTimer;

    function syncSubmitButton() {
      submitButton.disabled = !confirmationAcknowledgement?.checked || isSubmitting || !endpoint;
      if (confirmationAcknowledgement) confirmationAcknowledgement.disabled = isSubmitting;
    }

    if (confirmationAcknowledgement) {
      // Do not reuse a restored checkbox value for a new inquiry.
      confirmationAcknowledgement.checked = false;
      confirmationAcknowledgement.addEventListener('change', () => {
        clearStepError(panels.length - 1);
        syncSubmitButton();
      });
    }

    function showStep(step, options = {}) {
      const nextStep = Math.max(0, Math.min(step, panels.length - 1));
      if (nextStep !== currentStep && confirmationAcknowledgement) confirmationAcknowledgement.checked = false;
      currentStep = nextStep;
      syncSubmitButton();
      datePicker?.close();
      inquiryContent.sync();

      panels.forEach((panel, index) => {
        const active = index === nextStep;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      indicators.forEach((indicator, index) => {
        indicator.classList.toggle('is-current', index === nextStep);
        indicator.classList.toggle('is-complete', index < nextStep);
        if (index === nextStep) {
          indicator.setAttribute('aria-current', 'step');
        } else {
          indicator.removeAttribute('aria-current');
        }
      });

      if (nextStep === panels.length - 1) renderConfirmation();

      if (options.focus !== false) {
        const heading = panels[nextStep].querySelector('h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }

        const bounds = root.getBoundingClientRect();
        if (bounds.top < 0 || bounds.top > window.innerHeight * 0.65) {
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    function clearStepError(step) {
      const panel = panels[step];
      if (!panel) return;
      const message = panel.querySelector('[data-step-error]');
      if (message) {
        message.hidden = true;
        message.textContent = '';
      }
      panel.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
    }

    function setStepError(step, text, field) {
      const panel = panels[step];
      const message = panel && panel.querySelector('[data-step-error]');
      if (message) {
        message.textContent = text;
        message.hidden = false;
      }
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        if (typeof field.focus === 'function') field.focus();
      }
    }

    function validateStep(step) {
      clearStepError(step);
      const panel = panels[step];
      if (!panel) return true;
      inquiryContent.sync();

      if (step === panels.length - 1 && !confirmationAcknowledgement?.checked) {
        setStepError(step, '上記の内容をご確認のうえ、チェックを入れてください。', confirmationAcknowledgement);
        return false;
      }

      const invalidField = Array.from(panel.querySelectorAll('input[required], select[required], textarea[required]'))
        .find((field) => !field.matches('[data-image-input]') && (!String(field.value || '').trim() || !field.checkValidity()));

      if (invalidField) {
        const message = invalidField.validity.typeMismatch
          ? '正しい形式のメールアドレスを入力してください。'
          : '必須項目を入力してください。';
        setStepError(step, message, invalidField);
        return false;
      }

      const messageField = panel.querySelector('textarea[name="contact[body]"]');
      if (messageField && !inquiryContent.hasMessage()) {
        setStepError(step, 'お問い合わせ内容をご入力ください。', messageField);
        return false;
      }

      return true;
    }

    root.querySelectorAll('[data-next-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-step-panel]');
        const step = Number(panel.dataset.stepPanel);
        if (validateStep(step)) showStep(step + 1);
      });
    });

    root.querySelectorAll('[data-prev-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-step-panel]');
        showStep(Number(panel.dataset.stepPanel) - 1);
      });
    });

    panels.forEach((panel, step) => {
      panel.addEventListener('input', (event) => {
        if (event.target.matches('input, select, textarea')) clearStepError(step);
      });
    });

    const datePicker = setupCustomDatePicker(root);

    const attachmentController = setupImageAttachments(root);
    const productController = setupProductSearch(root);
    const orderController = setupOrderHistory(root, productController);
    const inquiryContent = setupInquiryContent(root, productController);
    setupLoginModal(root);

    function getConfirmationValue(field) {
      let value = String(field.value || '').trim();

      if (field.tagName === 'SELECT' && value) {
        const selectedOption = field.options[field.selectedIndex];
        value = selectedOption ? selectedOption.textContent.trim() : value;
      }

      const suffix = field.dataset.confirmSuffix || '';
      return value ? `${value}${suffix}` : '—';
    }

    function createConfirmationRow(label) {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      row.className = 'contact-inquiry__confirmation-row';
      term.textContent = label;
      row.append(term, description);
      confirmation.appendChild(row);
      return description;
    }

    function renderConfirmation() {
      if (!confirmation) return;

      confirmationUrls.forEach((url) => URL.revokeObjectURL(url));
      confirmationUrls = [];
      confirmation.replaceChildren();

      form.querySelectorAll('[data-confirm-label]').forEach((field) => {
        if (field.disabled) return;
        if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
        const description = createConfirmationRow(field.dataset.confirmLabel);
        description.textContent = getConfirmationValue(field);
      });

      const files = attachmentController.files();
      const imageDescription = createConfirmationRow('添付画像');

      if (!files.length) {
        imageDescription.textContent = '—';
        return;
      }

      const imageList = document.createElement('div');
      imageList.className = 'contact-inquiry__confirmation-images';
      files.forEach((file, index) => {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const caption = document.createElement('figcaption');
        const url = URL.createObjectURL(file);

        confirmationUrls.push(url);
        figure.className = 'contact-inquiry__confirmation-image';
        image.src = url;
        image.alt = `添付画像 ${index + 1}`;
        caption.textContent = file.name;
        figure.append(image, caption);
        imageList.appendChild(figure);
      });
      imageDescription.appendChild(imageList);
    }

    function validateAllSteps() {
      for (let step = 0; step < panels.length; step += 1) {
        if (!validateStep(step)) {
          showStep(step, { focus: false });
          const invalid = panels[step].querySelector('[aria-invalid="true"]');
          if (invalid && typeof invalid.focus === 'function') invalid.focus();
          return false;
        }
      }
      return true;
    }

    function setSubmitStatus(message, type) {
      submitStatus.textContent = message;
      submitStatus.className = `contact-inquiry__notice contact-inquiry__notice--${type}`;
      submitStatus.hidden = false;
    }

    function openSubmitModal() {
      if (submitReceipt) {
        submitReceipt.textContent = '';
        submitReceipt.hidden = true;
      }
      if (!submitModal) return;
      window.clearTimeout(submitModalCloseTimer);
      if (submitModalSending) submitModalSending.hidden = false;
      if (submitModalSuccess) submitModalSuccess.hidden = true;
      submitModal.classList.remove('is-complete');
      if (submitModalDialog) {
        const sendingTitle = submitModalSending && submitModalSending.querySelector('.contact-inquiry-submit-modal__title');
        const sendingText = submitModalSending && submitModalSending.querySelector('.contact-inquiry-submit-modal__text');
        submitModalDialog.setAttribute('aria-busy', 'true');
        if (sendingTitle) submitModalDialog.setAttribute('aria-labelledby', sendingTitle.id);
        if (sendingText) submitModalDialog.setAttribute('aria-describedby', sendingText.id);
      }
      submitModalOpenedAt = performance.now();
      submitModal.hidden = false;
      submitModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('contact-inquiry-submit-modal-open');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          submitModal.classList.add('is-open');
          if (submitModalDialog) submitModalDialog.focus({ preventScroll: true });
        });
      });
    }

    function showSubmitSuccessModal(receiptNumber) {
      if (submitReceipt) {
        submitReceipt.textContent = receiptNumber ? `受付番号：${receiptNumber}` : '';
        submitReceipt.hidden = !receiptNumber;
      }
      if (!submitModal || !submitModalSuccess) return;
      if (submitModalSending) submitModalSending.hidden = true;
      submitModalSuccess.hidden = false;
      submitModal.classList.add('is-complete');

      if (submitModalDialog) {
        const successTitle = submitModalSuccess.querySelector('.contact-inquiry-submit-modal__title');
        const successText = submitModalSuccess.querySelector('.contact-inquiry-submit-modal__text[id]');
        submitModalDialog.setAttribute('aria-busy', 'false');
        if (successTitle) submitModalDialog.setAttribute('aria-labelledby', successTitle.id);
        if (successText) submitModalDialog.setAttribute('aria-describedby', successText.id);
      }

      window.requestAnimationFrame(() => {
        if (submitAgainButton) submitAgainButton.focus({ preventScroll: true });
      });
    }

    async function waitForSubmitModalAnimation() {
      if (!submitModalOpenedAt) return;
      const remaining = SUBMIT_MODAL_MIN_VISIBLE - (performance.now() - submitModalOpenedAt);
      if (remaining > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remaining));
      }
    }

    function closeSubmitModal() {
      if (!submitModal) return;
      submitModal.classList.remove('is-open');
      submitModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('contact-inquiry-submit-modal-open');
      submitModalOpenedAt = 0;
      window.clearTimeout(submitModalCloseTimer);
      submitModalCloseTimer = window.setTimeout(() => {
        submitModal.hidden = true;
        submitModal.classList.remove('is-complete');
      }, 420);
    }

    if (submitModal) {
      submitModal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = Array.from(submitModal.querySelectorAll('a[href], button:not([disabled])'))
          .filter((element) => !element.hidden && element.offsetParent !== null);
        if (!focusable.length) {
          event.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    if (submitAgainButton) {
      submitAgainButton.addEventListener('click', () => {
        submitStatus.hidden = true;
        closeSubmitModal();
        showStep(0, { focus: false });
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => {
          const firstField = panels[0] && panels[0].querySelector('button, input:not([type="hidden"]), select, textarea');
          if (firstField) firstField.focus({ preventScroll: true });
        }, 120);
      });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (isSubmitting) {
        return;
      }

      // Enter on an input advances the current step; only confirmation may send.
      if (currentStep < panels.length - 1) {
        if (validateStep(currentStep)) showStep(currentStep + 1);
        return;
      }

      if (!validateAllSteps()) {
        return;
      }

      if (!endpoint) {
        setSubmitStatus('現在、お問い合わせの送信設定を準備中です。dress@tweed.co.jp へ直接ご連絡ください。', 'error');
        return;
      }

      isSubmitting = true;
      syncSubmitButton();
      submitButton.setAttribute('aria-busy', 'true');
      submitLabel.textContent = '送信中…';
      submitStatus.hidden = true;
      openSubmitModal();
      let submissionSucceeded = false;
      let submittedReceiptNumber = '';

      try {
        const formData = new FormData(form);
        formData.delete('images[]');
        attachmentController.files().forEach((file) => formData.append('images[]', file, file.name));

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        let responseBody = null;
        if ((response.headers.get('content-type') || '').includes('application/json')) {
          responseBody = await response.json().catch(() => null);
        }

        if (!response.ok || !responseBody || responseBody.success !== true) {
          throw new Error(responseBody && responseBody.message ? responseBody.message : '送信に失敗しました。');
        }

        if (typeof responseBody.receiptNumber === 'string' && /^CIQ#\d{6,}$/.test(responseBody.receiptNumber)) {
          submittedReceiptNumber = responseBody.receiptNumber;
        }

        form.reset();
        datePicker?.reset();
        attachmentController.reset();
        productController.reset();
        orderController.reset();
        inquiryContent.reset();
        panels.forEach((panel, step) => clearStepError(step));
        showStep(0, { focus: false });
        setSubmitStatus(responseBody && responseBody.message ? responseBody.message : root.dataset.successMessage, 'success');
        submissionSucceeded = true;
      } catch (error) {
        setSubmitStatus(error.message || '送信できませんでした。時間をおいて再度お試しください。', 'error');
      } finally {
        await waitForSubmitModalAnimation();
        if (submissionSucceeded) showSubmitSuccessModal(submittedReceiptNumber);
        else closeSubmitModal();
        isSubmitting = false;
        syncSubmitButton();
        submitButton.removeAttribute('aria-busy');
        submitLabel.textContent = '送信';
        if (!submissionSucceeded) {
          submitStatus.setAttribute('tabindex', '-1');
          submitStatus.focus({ preventScroll: true });
        }
      }
    });

    showStep(currentStep, { focus: false });
  }

  function setupInquiryContent(root, productController) {
    const message = root.querySelector('textarea[name="contact[body]"]');
    if (!message) return { sync() {}, reset() {}, hasMessage: () => true };
    const order = root.querySelector('[data-order-number]');
    const title = root.querySelector('[data-product-title]');
    const query = root.querySelector('[data-product-query]');
    const orderSelect = root.querySelector('[data-order-select]');
    const manualToggle = root.querySelector('[data-manual-product-toggle]');
    let generatedHeader = '';

    function singleLine(field) {
      return String(field?.value || '').replace(/[\r\n]+/g, ' ').trim();
    }

    function customerMessage() {
      const value = String(message.value || '');
      // Remove only the exact header generated by this controller, never arbitrary
      // customer-written lines that happen to start with the same labels.
      if (generatedHeader && (value === generatedHeader || value.startsWith(`${generatedHeader}\n`))) {
        return value.slice(generatedHeader.length).replace(/^\n{1,2}/, '');
      }
      return value;
    }

    function sync() {
      const body = customerMessage();
      const orderNumber = singleLine(order);
      const productName = singleLine(title) || (query && !query.disabled ? singleLine(query) : '');
      const header = [
        orderNumber ? `注文番号：${orderNumber}` : '',
        productName ? `品番：${productName}` : ''
      ].filter(Boolean).join('\n');
      const value = header ? `${header}\n\n${body}` : body;
      generatedHeader = header;
      if (message.value !== value) message.value = value;
    }

    function onBasicFieldChange(event) {
      if ([order, title, query, orderSelect, manualToggle].includes(event.target)) sync();
    }
    root.addEventListener('input', onBasicFieldChange);
    root.addEventListener('change', onBasicFieldChange);
    productController?.onChange?.(sync);
    sync();
    return {
      sync,
      hasMessage: () => Boolean(customerMessage().trim()),
      reset() {
        generatedHeader = '';
        sync();
      }
    };
  }

  function setupCustomDatePicker(root) {
    const picker = root.querySelector('[data-custom-date-picker]');
    if (!picker) return;

    const displayInput = picker.querySelector('[data-date-display]');
    const valueInput = picker.querySelector('[data-wear-date]');
    const toggleButton = picker.querySelector('[data-date-toggle]');
    const calendar = picker.querySelector('[data-date-calendar]');
    const monthLabel = picker.querySelector('[data-date-month]');
    const dateGrid = picker.querySelector('[data-date-grid]');
    const previousButton = picker.querySelector('[data-date-prev]');
    const nextButton = picker.querySelector('[data-date-next]');
    const clearButton = picker.querySelector('[data-date-clear]');
    if (!displayInput || !valueInput || !toggleButton || !calendar || !monthLabel || !dateGrid) return;

    let today;
    let minimumDate;

    function refreshDateBoundary() {
      today = new Date();
      today.setHours(0, 0, 0, 0);
      minimumDate = new Date(today);
      minimumDate.setDate(minimumDate.getDate() + 4);
    }

    refreshDateBoundary();

    function dateKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    function parseDateKey(value) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return null;
      const parts = String(value).split('-').map(Number);
      if (parts.length !== 3 || !parts.every(Number.isFinite)) return null;
      const date = new Date(parts[0], parts[1] - 1, parts[2]);
      return Number.isNaN(date.getTime()) || dateKey(date) !== value ? null : date;
    }

    function sameDate(left, right) {
      return Boolean(left && right) && dateKey(left) === dateKey(right);
    }

    let selectedDate = parseDateKey(valueInput.value);
    if (selectedDate && selectedDate < minimumDate) selectedDate = null;
    let visibleMonth = new Date(
      (selectedDate || minimumDate).getFullYear(),
      (selectedDate || minimumDate).getMonth(),
      1
    );

    function updateDisplay() {
      valueInput.value = selectedDate ? dateKey(selectedDate) : '';
      displayInput.value = selectedDate
        ? `${selectedDate.getFullYear()}年${selectedDate.getMonth() + 1}月${selectedDate.getDate()}日`
        : '';
    }

    function closeCalendar(returnFocus = false) {
      calendar.hidden = true;
      picker.classList.remove('is-open');
      displayInput.setAttribute('aria-expanded', 'false');
      toggleButton.setAttribute('aria-expanded', 'false');
      if (returnFocus) toggleButton.focus();
    }

    function renderCalendar() {
      const year = visibleMonth.getFullYear();
      const month = visibleMonth.getMonth();
      const firstWeekday = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      monthLabel.textContent = `${year}年 ${month + 1}月`;
      dateGrid.replaceChildren();

      if (previousButton) {
        previousButton.disabled = visibleMonth <= new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
      }

      for (let index = 0; index < firstWeekday; index += 1) {
        const blank = document.createElement('span');
        blank.className = 'contact-inquiry__calendar-blank';
        blank.setAttribute('aria-hidden', 'true');
        dateGrid.appendChild(blank);
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(year, month, day);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'contact-inquiry__calendar-day';
        button.textContent = String(day);
        button.dataset.dateValue = dateKey(date);
        button.setAttribute('aria-label', `${year}年${month + 1}月${day}日`);
        button.disabled = date < minimumDate;
        button.classList.toggle('is-today', sameDate(date, today));
        button.classList.toggle('is-selected', sameDate(date, selectedDate));
        button.setAttribute('aria-pressed', sameDate(date, selectedDate) ? 'true' : 'false');
        button.addEventListener('click', () => {
          refreshDateBoundary();
          if (date < minimumDate) {
            openCalendar();
            return;
          }
          selectedDate = date;
          updateDisplay();
          valueInput.dispatchEvent(new Event('input', { bubbles: true }));
          valueInput.dispatchEvent(new Event('change', { bubbles: true }));
          closeCalendar(true);
        });
        dateGrid.appendChild(button);
      }
    }

    function openCalendar(focusDay = false) {
      refreshDateBoundary();
      if (selectedDate && selectedDate < minimumDate) selectedDate = null;
      if (visibleMonth < new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1)) {
        visibleMonth = new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
      }
      updateDisplay();
      calendar.hidden = false;
      picker.classList.add('is-open');
      displayInput.setAttribute('aria-expanded', 'true');
      toggleButton.setAttribute('aria-expanded', 'true');
      renderCalendar();
      window.requestAnimationFrame(() => {
        if (calendar.hidden || !focusDay) return;
        const focusTarget = calendar.querySelector('.is-selected:not(:disabled), .is-today:not(:disabled), .contact-inquiry__calendar-day:not(:disabled)');
        if (focusTarget) focusTarget.focus();
      });
    }

    function toggleCalendar(event) {
      // Pointer/touch opening must not draw a keyboard focus ring on a date.
      const fromKeyboard = event?.type === 'keydown' || event?.detail === 0;
      if (calendar.hidden) openCalendar(fromKeyboard);
      else closeCalendar();
    }

    displayInput.addEventListener('click', toggleCalendar);
    displayInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleCalendar(event);
      }
    });
    toggleButton.addEventListener('click', toggleCalendar);

    if (previousButton) {
      previousButton.addEventListener('click', () => {
        if (previousButton.disabled) return;
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1);
        renderCalendar();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);
        renderCalendar();
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        selectedDate = null;
        refreshDateBoundary();
        visibleMonth = new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
        updateDisplay();
        valueInput.dispatchEvent(new Event('input', { bubbles: true }));
        valueInput.dispatchEvent(new Event('change', { bubbles: true }));
        closeCalendar(true);
      });
    }

    calendar.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCalendar(true);
      }
    });

    document.addEventListener('click', (event) => {
      if (!picker.contains(event.target)) closeCalendar();
    });

    updateDisplay();
    renderCalendar();
    return {
      close: closeCalendar,
      reset() {
        selectedDate = null;
        refreshDateBoundary();
        visibleMonth = new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
        updateDisplay();
        closeCalendar();
        renderCalendar();
      }
    };
  }

  function setupLoginModal(root) {
    const trigger = root.querySelector('[data-open-login]');
    const modal = root.querySelector('[data-login-modal]');
    if (!trigger || !modal) return;
    const dialog = modal.querySelector('[data-login-dialog]');
    const email = modal.querySelector('[data-login-email]');
    const password = modal.querySelector('[data-login-password]');
    const closeButton = modal.querySelector('[data-close-login]');
    const inquiryContent = root.querySelector('.contact-inquiry__inner');
    const inquiryEmail = root.querySelector('input[name="contact[email]"]');
    let closeTimer;
    let previouslyInert = false;

    function open() {
      window.clearTimeout(closeTimer);
      if (modal.hidden) previouslyInert = Boolean(inquiryContent && inquiryContent.inert);
      modal.hidden = false;
      if (inquiryContent) inquiryContent.inert = true;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('contact-inquiry-login-modal-open');
      if (!email.value && inquiryEmail) email.value = inquiryEmail.value;
      window.requestAnimationFrame(() => {
        if (trigger.getAttribute('aria-expanded') !== 'true') return;
        modal.classList.add('is-open');
        email.focus({ preventScroll: true });
      });
    }

    function close() {
      modal.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      // Do not retain a password after dismissing the dialog.
      password.value = '';
      if (inquiryContent) inquiryContent.inert = previouslyInert;
      document.body.classList.remove('contact-inquiry-login-modal-open');
      trigger.focus({ preventScroll: true });
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => { modal.hidden = true; }, 200);
    }

    trigger.addEventListener('click', (event) => {
      // Preserve the real login URL for new tabs and browsers without this script.
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      open();
    });
    closeButton.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      // Keep normal tab navigation in the dialog; do not trap external CAPTCHA challenges.
      const focusable = Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), iframe'))
        .filter((element) => !element.hidden && element.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    });
    // No submit handler: use Shopify's native POST and CAPTCHA, never the email API.
    if (modal.querySelector('[data-login-errors]')) open();
  }

  function setupOrderHistory(root, productController) {
    const history = root.querySelector('[data-order-history]');
    if (!history || !productController) return { reset() {} };
    const number = root.querySelector('[data-order-number]');
    const ordersSelect = history.querySelector('[data-order-select]');
    const productField = root.querySelector('[data-order-product-field]');
    const productsSelect = root.querySelector('[data-order-product-select]');
    const manualField = root.querySelector('[data-manual-product-field]');
    const manualToggleField = root.querySelector('[data-manual-product-toggle-field]');
    const manualToggle = root.querySelector('[data-manual-product-toggle]');
    const query = root.querySelector('[data-product-query]');
    const searchButton = root.querySelector('[data-product-search-button]');
    const more = history.querySelector('[data-orders-more]');
    const status = history.querySelector('[data-orders-status]');
    const orders = new Map();
    let nextUrl = '';
    let pendingRequest;

    function option(value, label) {
      const node = document.createElement('option');
      node.value = value;
      node.textContent = label;
      return node;
    }

    function clearProducts() {
      // Also cancels pending manual searches before their UI is hidden.
      productController.reset();
      productsSelect.replaceChildren(option('', '商品を選択してください'));
    }

    function syncProductMode() {
      const hasOrder = orders.has(ordersSelect.value);
      if (!hasOrder) manualToggle.checked = false;
      const manual = !hasOrder || manualToggle.checked;
      manualField.hidden = !manual;
      query.disabled = !manual;
      searchButton.disabled = !manual;
      productField.hidden = manual;
      productsSelect.disabled = manual || !(orders.get(ordersSelect.value) || []).length;
      manualToggleField.hidden = !hasOrder;
    }

    function readOrders(source) {
      source.querySelectorAll('[data-history-order]').forEach((node) => {
        const name = node.dataset.number;
        if (!name || orders.has(name)) return;
        const titles = [...new Set(Array.from(node.querySelectorAll('[data-history-product]'))
          .map((item) => item.dataset.title).filter(Boolean))];
        orders.set(name, titles);
        ordersSelect.appendChild(option(name, node.dataset.label || name));
      });
      nextUrl = source.dataset.nextUrl || '';
      more.hidden = !nextUrl;
      status.textContent = orders.size ? '' : '注文履歴がありません。注文を選択せずにお進みいただけます。';
    }

    const initialSource = history.querySelector('[data-order-history-source]');
    if (initialSource) {
      readOrders(initialSource);
      initialSource.remove();
    }
    syncProductMode();

    ordersSelect.addEventListener('change', () => {
      clearProducts();
      manualToggle.checked = false;
      number.value = ordersSelect.value;
      const titles = orders.get(ordersSelect.value) || [];
      titles.forEach((title, index) => productsSelect.appendChild(option(String(index), title)));
      if (orders.has(ordersSelect.value) && !titles.length) {
        productsSelect.replaceChildren(option('', 'この注文に商品情報がありません'));
      }
      syncProductMode();
    });

    manualToggle.addEventListener('change', () => {
      productController.reset();
      syncProductMode();
    });

    number.addEventListener('input', () => {
      if (number.value !== ordersSelect.value) {
        ordersSelect.value = '';
        clearProducts();
        syncProductMode();
      }
    });

    productsSelect.addEventListener('change', () => {
      if (productsSelect.disabled) return;
      const titles = orders.get(ordersSelect.value) || [];
      const title = productsSelect.value === '' ? '' : titles[Number(productsSelect.value)];
      if (!title) {
        productController.reset();
        return;
      }
      productController.select({ title }, 'order');
    });

    productController.onChange((source) => {
      if (source === 'order') return;
      productsSelect.value = '';
    });

    more.addEventListener('click', async () => {
      if (!nextUrl || pendingRequest) return;
      const controller = new AbortController();
      pendingRequest = controller;
      more.disabled = true;
      status.textContent = '注文履歴を読み込み中…';
      try {
        const url = new URL(nextUrl, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
          throw new Error('Invalid order history URL');
        }
        url.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetchWithTimeout(url.toString(), {
          credentials: 'same-origin', cache: 'no-store', redirect: 'error',
          headers: { Accept: 'text/html' }, signal: controller.signal
        });
        if (!response.ok) throw new Error('Order history unavailable');
        const html = await response.text();
        if (controller.signal.aborted || pendingRequest !== controller) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('[data-order-history-source]');
        if (!source) throw new Error('Order history session unavailable');
        const previousSize = orders.size;
        readOrders(source);
        status.textContent = `${orders.size - previousSize}件の注文を追加しました。`;
      } catch (error) {
        if (controller.signal.aborted) return;
        status.textContent = '注文履歴を読み込めませんでした。ログイン状態を確認して再度お試しください。注文を選択せずにお進みいただくこともできます。';
      } finally {
        if (pendingRequest === controller) {
          pendingRequest = null;
          more.disabled = false;
        }
      }
    });

    return {
      reset() {
        if (pendingRequest) pendingRequest.abort();
        pendingRequest = null;
        more.disabled = false;
        ordersSelect.value = '';
        number.value = '';
        clearProducts();
        syncProductMode();
        status.textContent = orders.size ? '' : '注文履歴がありません。注文を選択せずにお進みいただけます。';
      }
    };
  }

  function setupProductSearch(root) {
    const search = root.querySelector('[data-product-search]');
    if (!search) return;

    const input = search.querySelector('[data-product-query]');
    const results = search.querySelector('[data-product-results]');
    const status = search.querySelector('[data-search-status]');
    const selected = search.querySelector('[data-selected-product]');
    const selectedName = search.querySelector('[data-selected-product-name]');
    const clearButton = search.querySelector('[data-clear-product]');
    const searchButton = search.querySelector('[data-product-search-button]');
    const titleField = search.querySelector('[data-product-title]');
    const searchUrl = root.dataset.searchUrl || '/search';
    let debounceTimer;
    let abortController;
    let products = [];
    let activeIndex = -1;
    let selectedTitle = titleField.value;
    const selectionListeners = [];

    function closeResults() {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function setActiveResult(index) {
      const options = Array.from(results.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      activeIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) => {
        option.classList.toggle('is-focused', optionIndex === activeIndex);
        option.setAttribute('aria-selected', optionIndex === activeIndex ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', options[activeIndex].id);
      options[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    function chooseProduct(product, source = 'search') {
      window.clearTimeout(debounceTimer);
      if (abortController) abortController.abort();
      searchButton.disabled = false;
      selectedTitle = product.title;
      input.value = product.title;
      titleField.value = product.title;
      selectedName.textContent = `選択中：${product.title}`;
      selected.hidden = false;
      status.textContent = '';
      closeResults();
      selectionListeners.forEach((listener) => listener(source));
    }

    function renderProducts(items) {
      products = items;
      results.replaceChildren();

      if (!items.length) {
        closeResults();
        status.textContent = '該当する商品が見つかりませんでした。';
        return;
      }

      items.forEach((product, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        const copy = document.createElement('span');
        const title = document.createElement('strong');

        item.setAttribute('role', 'none');
        button.id = `${results.id}-option-${index}`;
        button.className = 'contact-inquiry__result-button';
        button.type = 'button';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');

        if (product.featured_image && product.featured_image.url) {
          const image = document.createElement('img');
          image.className = 'contact-inquiry__result-image';
          image.src = product.featured_image.url;
          image.alt = '';
          image.loading = 'lazy';
          button.appendChild(image);
        } else if (product.image) {
          const image = document.createElement('img');
          image.className = 'contact-inquiry__result-image';
          image.src = product.image;
          image.alt = '';
          image.loading = 'lazy';
          button.appendChild(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'contact-inquiry__result-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          placeholder.textContent = '—';
          button.appendChild(placeholder);
        }

        copy.className = 'contact-inquiry__result-copy';
        title.textContent = product.title;
        copy.appendChild(title);
        button.appendChild(copy);
        button.addEventListener('click', () => chooseProduct(product));
        item.appendChild(button);
        results.appendChild(item);
      });

      status.textContent = `${items.length}件の商品候補があります。`;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    async function fetchPredictiveProducts(query, signal) {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'show',
        'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku,variants.barcode'
      });
      const routesRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      const response = await fetchWithTimeout(`${routesRoot}search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const payload = await response.json();
      return payload.resources.results.products || [];
    }

    async function fetchGraphqlProducts(query, signal) {
      const graphqlQuery = `
        query ContactProductSearch($query: String!) {
          search(
            first: 8
            query: $query
            types: [PRODUCT]
            prefix: LAST
            unavailableProducts: SHOW
          ) {
            nodes {
              __typename
              ... on Product {
                id
                title
                handle
                vendor
                onlineStoreUrl
                featuredImage { url }
                variants(first: 20) {
                  nodes { id title sku barcode }
                }
              }
            }
          }
        }
      `;
      const response = await fetchWithTimeout(`${window.location.origin}/api/2026-07/graphql.json`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: graphqlQuery, variables: { query } }),
        signal
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload || payload.errors || !payload.data || !payload.data.search) {
        const message = payload && payload.errors && payload.errors[0] && payload.errors[0].message;
        throw new Error(message || `Storefront API failed: ${response.status}`);
      }

      const routesRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      return payload.data.search.nodes
        .filter((product) => product && product.__typename === 'Product')
        .map((product) => {
          const productId = String(product.id || '').split('/').pop();
          const productUrl = product.onlineStoreUrl || `${routesRoot}products/${product.handle}`;
          const variants = product.variants && Array.isArray(product.variants.nodes)
            ? product.variants.nodes.map((variant) => {
              const variantId = String(variant.id || '').split('/').pop();
              const separator = productUrl.includes('?') ? '&' : '?';
              return {
                id: variantId,
                title: variant.title,
                sku: variant.sku,
                barcode: variant.barcode,
                url: variantId ? `${productUrl}${separator}variant=${variantId}` : productUrl
              };
            })
            : [];

          return {
            id: productId,
            title: product.title,
            vendor: product.vendor,
            url: productUrl,
            image: product.featuredImage ? product.featuredImage.url : null,
            variants
          };
        });
    }

    async function fetchStorefrontProducts(query, signal) {
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('view', 'contact-products');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'show');

      const response = await fetchWithTimeout(url.toString(), {
        headers: { Accept: 'text/html' },
        signal
      });
      if (!response.ok) throw new Error(`Storefront search failed: ${response.status}`);
      const payload = JSON.parse(await response.text());
      if (!payload || !Array.isArray(payload.products)) throw new Error('Invalid storefront search response');
      return payload.products;
    }

    async function fetchProducts(query) {
      if (abortController) abortController.abort();
      const requestController = new AbortController();
      abortController = requestController;
      closeResults();
      status.textContent = '検索中…';
      searchButton.disabled = true;

      try {
        let items;
        try {
          items = await fetchGraphqlProducts(query, requestController.signal);
        } catch (graphqlError) {
          if (graphqlError.name === 'AbortError') throw graphqlError;
          try {
            items = await fetchStorefrontProducts(query, requestController.signal);
          } catch (storefrontError) {
            if (storefrontError.name === 'AbortError') throw storefrontError;
            items = await fetchPredictiveProducts(query, requestController.signal);
          }
        }
        if (requestController.signal.aborted || abortController !== requestController) return;
        renderProducts(items);
      } catch (error) {
        if (error.name === 'AbortError') return;
        closeResults();
        status.textContent = '商品検索に失敗しました。時間をおいて再度検索してください。';
      } finally {
        if (abortController === requestController) searchButton.disabled = false;
      }
    }

    function searchCurrentValue() {
      window.clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (!query) {
        status.textContent = '品番・SKU・バーコード・商品名を入力してください。';
        input.focus();
        return;
      }
      fetchProducts(query);
    }

    input.addEventListener('input', () => {
      if (abortController) abortController.abort();
      if (selectedTitle && input.value !== selectedTitle) clearSelectedProduct(root, { keepQuery: true });
      window.clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (query.length < 2) {
        if (abortController) abortController.abort();
        products = [];
        results.replaceChildren();
        closeResults();
        status.textContent = '';
        return;
      }
      debounceTimer = window.setTimeout(() => fetchProducts(query), 280);
    });

    searchButton.addEventListener('click', searchCurrentValue);

    input.addEventListener('keydown', (event) => {
      if (results.hidden && (event.key === 'ArrowDown' || event.key === 'ArrowUp') && products.length) {
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveResult(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveResult(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0 && !results.hidden) {
        event.preventDefault();
        chooseProduct(products[activeIndex]);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        searchCurrentValue();
      } else if (event.key === 'Escape') {
        closeResults();
      }
    });

    clearButton.addEventListener('click', () => {
      clearSelectedProduct(root);
      selectedTitle = '';
      input.focus();
    });

    document.addEventListener('click', (event) => {
      if (!search.contains(event.target)) closeResults();
    });

    search._clearProductState = () => {
      window.clearTimeout(debounceTimer);
      if (abortController) abortController.abort();
      searchButton.disabled = false;
      selectedTitle = '';
      products = [];
      status.textContent = '';
      closeResults();
      selectionListeners.forEach((listener) => listener('clear'));
    };
    return {
      reset: () => clearSelectedProduct(root),
      select: chooseProduct,
      onChange: (listener) => selectionListeners.push(listener)
    };
  }

  function clearSelectedProduct(root, options = {}) {
    const search = root.querySelector('[data-product-search]');
    if (!search) return;
    const input = search.querySelector('[data-product-query]');
    if (!options.keepQuery) input.value = '';
    search.querySelector('[data-product-title]').value = '';
    search.querySelector('[data-selected-product]').hidden = true;
    if (typeof search._clearProductState === 'function') search._clearProductState();
  }

  function setupImageAttachments(root) {
    const input = root.querySelector('[data-image-input]');
    const previews = root.querySelector('[data-image-previews]');
    const errorOutput = root.querySelector('[data-file-error]');
    let files = [];
    let previewUrls = [];

    function showError(message) {
      errorOutput.textContent = message;
      errorOutput.hidden = !message;
    }

    function syncInputFiles() {
      if (typeof DataTransfer === 'undefined') return;
      const transfer = new DataTransfer();
      files.forEach((file) => transfer.items.add(file));
      input.files = transfer.files;
    }

    function renderPreviews() {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls = [];
      previews.replaceChildren();

      files.forEach((file, index) => {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const remove = document.createElement('button');
        const url = URL.createObjectURL(file);

        previewUrls.push(url);
        figure.className = 'contact-inquiry__preview';
        image.src = url;
        image.alt = `添付画像 ${index + 1}`;
        remove.type = 'button';
        remove.textContent = '×';
        remove.setAttribute('aria-label', `${file.name}を削除`);
        remove.addEventListener('click', () => {
          files.splice(index, 1);
          syncInputFiles();
          renderPreviews();
          showError('');
        });

        figure.append(image, remove);
        previews.appendChild(figure);
      });
    }

    input.addEventListener('change', () => {
      const incoming = Array.from(input.files || []);
      const known = new Set(files.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      const messages = [];

      incoming.forEach((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (known.has(key)) return;
        if (!file.type.startsWith('image/')) {
          messages.push(`${file.name}は画像ファイルではありません。`);
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          messages.push(`${file.name}は10MBを超えています。`);
          return;
        }
        const totalSize = files.reduce((sum, currentFile) => sum + currentFile.size, 0);
        if (totalSize + file.size > MAX_TOTAL_FILE_SIZE) {
          messages.push('画像の合計容量は15MB以内にしてください。');
          return;
        }
        if (files.length >= MAX_FILES) {
          messages.push(`画像は最大${MAX_FILES}枚までです。`);
          return;
        }
        known.add(key);
        files.push(file);
      });

      syncInputFiles();
      renderPreviews();
      showError(messages.join(' '));
    });

    return {
      files: () => files.slice(),
      reset: () => {
        files = [];
        input.value = '';
        renderPreviews();
        showError('');
      }
    };
  }

  function initializeAll(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initializeContactForm(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initializeContactForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
